/**
 * Настройки, которые админ меняет на ходу (§5.7).
 *
 * Всё, что здесь, менять редеплоем нельзя: лимиты, сроки дедупа, порог дозвона,
 * ретеншн записей. Значения версионируются в settings_history — те из них,
 * что влияют на исторические цифры, отчёт потом подписывает применённым
 * значением, иначе месячный отчёт станет смесью двух правил (§7.5).
 */
import { z } from 'zod';
import { isoDateTimeSchema } from './common.dto.js';
/** Ключи настроек: закрытый список, произвольные ключи не заводятся из UI */
export const settingKeySchema = z.enum([
    'timezone',
    'business_day.start',
    'dedup.requeue_after_days',
    'dedup.lead_lock_days',
    'attribution.window_days',
    'assignment.ttl_minutes',
    'assignment.grace_after_call_minutes',
    'ndz.owner_release_hours',
    'distribute.max_items_per_run',
    'distribute.fresh_today_only',
    'telephony.reached_min_sec',
    'telephony.ice_grace_sec',
    'telephony.manual_calls_per_hour',
    'telephony.manual_other_numbers_per_day',
    'telephony.trunk_rotation',
    // Тариф оператора в копейках: цена набора снимается за каждую попытку,
    // цена минуты — за разговор с посекундной тарификацией
    'telephony.price_attempt_kop',
    'telephony.price_minute_kop',
    'recording.retention_days',
    'recording.stop_when_disk_pct',
    // Вознаграждение диспетчера за лид, в рублях. 0 — заработок не считается
    'pay.per_lead_rub',
    'robot.enabled',
    'robot.handover_hour_msk',
    'robot.hold_day_end',
    'robot.price_minute_kop',
    'robot.billing_step_sec',
    'distribute.first_round_only',
    'robot.on_fail',
    'crm.lead_relink_days',
    'expire_label_code',
    // Окно звонка по местному времени абонента
    'calling.window_enabled',
    'calling.window_start_hour',
    'calling.window_end_hour',
    'calling.unknown_tz_as_msk',
    // Карусель дозвона (§7.11): включение, темп, дневной потолок и запуск на
    // выбранных диспетчеров. Правятся с панели «Карусель» на Обзоре
    'carousel.enabled',
    'carousel.lines_per_free',
    'carousel.daily_cap',
    'carousel.dispatcher_ids',
]);
/** Значение хранится в jsonb: число, строка или флаг — тип проверяет домен по ключу */
export const settingValueSchema = z.union([z.string(), z.number(), z.boolean()]);
export const settingRowSchema = z.object({
    key: settingKeySchema,
    value: settingValueSchema,
    updatedAt: isoDateTimeSchema.nullable(),
    updatedByName: z.string().nullable(),
});
export const settingsResponseSchema = z.object({ items: z.array(settingRowSchema) });
/** Путь /:key — ключ обязан быть из закрытого списка, иначе 400 ещё до домена */
export const settingKeyParamSchema = z.object({ key: settingKeySchema });
/** PUT /api/settings/:key — одна настройка за раз, действие пишется в audit_log */
export const updateSettingBodySchema = z.object({
    value: settingValueSchema,
    /** Зачем меняем: попадает в settings_history и в аудит */
    note: z.string().max(300).optional(),
});
export const updateSettingResponseSchema = z.object({
    key: settingKeySchema,
    value: settingValueSchema,
    /**
     * Смена порога дозвона запускает пересчёт витрин за затронутый период (§7.5).
     * Здесь — признак, что пересчёт поставлен в очередь.
     */
    recalcScheduled: z.boolean(),
});
/** GET /api/settings/:key/history — чем считались прошлые отчёты */
export const settingHistoryRowSchema = z.object({
    value: settingValueSchema,
    validFrom: isoDateTimeSchema,
    changedByName: z.string().nullable(),
});
/** Схема кругов: сколько всего кругов и какая пауза между ними (§5.4) */
export const roundStepSchema = z.object({
    roundNumber: z.number().int().min(1).max(20),
    minDelayMinutes: z.number().int().min(0).max(60 * 24 * 30),
    /** Оставить контакт владельцу: так работает «НДЗ только своему» */
    keepOwner: z.boolean(),
});
export const roundSchemeSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    isDefault: z.boolean(),
    robotEnabled: z.boolean(),
    robotProvider: z.string().nullable(),
    /** Окно, в котором НДЗ разрешено всплывать: без него круг проскакивает ночью */
    workWindow: z.object({ from: z.string(), to: z.string() }),
    isActive: z.boolean(),
    steps: z.array(roundStepSchema),
});
export const updateRoundSchemeBodySchema = z.object({
    name: z.string().min(2).max(120).optional(),
    robotEnabled: z.boolean().optional(),
    robotProvider: z.string().max(80).nullable().optional(),
    workWindow: z.object({ from: z.string(), to: z.string() }).optional(),
    /** Шаги задаются целиком: максимальный круг — это максимум round_number, отдельного поля нет */
    steps: z.array(roundStepSchema).min(1).max(20).optional(),
});
//# sourceMappingURL=settings.dto.js.map